import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Wifi, Car, Wind, Droplets, Plane, Users, UtensilsCrossed, BellRing, Shirt, MapPin, Zap, Shield, CheckCircle2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { facilities } from '../data/mock';

const iconMap = {
  Wifi,
  Car,
  Wind,
  Droplets,
  Plane,
  Users,
  UtensilsCrossed,
  BellRing,
  Shirt,
  MapPin,
  Zap,
  Shield,
};

const Facilities = () => {
  return (
    <>
      <Helmet>
        <title>{`Hotel Facilities | Hotel Suvidha Deluxe Haridwar`}</title>
        <meta name="description" content="Discover modern facilities at Hotel Suvidha Deluxe including WiFi, parking, room service, and comfort." />
      </Helmet>
    <div className="min-h-screen pt-32 pb-20">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1566073771259-6a8506099945?w=1920&q=80)', backgroundSize: 'cover', backgroundPosition: 'center' }}></div>
        </div>
        <div className="container mx-auto px-4 relative z-10 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 font-serif text-navy-900">Premium Facilities</h1>
          <p className="text-xl text-gold-400 max-w-3xl mx-auto leading-relaxed">
            Enjoy world-class amenities designed to make your stay comfortable, convenient, and memorable
          </p>
        </div>
      </section>

      {/* Facilities Grid */}
      <section className="container mx-auto px-4 -mt-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {facilities.map((facility) => {
            const IconComponent = iconMap[facility.icon] || CheckCircle2;
            return (
              <Card key={facility.id} className="border-0 shadow-xl hover:shadow-2xl transition-all group overflow-hidden">
                <CardContent className="p-8">
                  <div className="w-20 h-20 bg-gradient-to-br from-gold-100 to-gold-50 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                    <IconComponent className="text-gold-600" size={36} />
                  </div>
                  <h3 className="text-2xl font-bold text-navy-900 mb-3">{facility.name}</h3>
                  <p className="text-gray-600 leading-relaxed">{facility.description}</p>
                </CardContent>
                <div className="h-1 bg-gradient-to-r from-gold-500 to-gold-600 transform scale-x-0 group-hover:scale-x-100 transition-transform"></div>
              </Card>
            );
          })}
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-20 mt-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-navy-900 mb-4 font-serif">Additional Services</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              We go the extra mile to ensure your comfort
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <Card className="border-0 shadow-lg">
              <CardContent className="p-8">
                <h3 className="text-xl font-bold text-navy-900 mb-4">Concierge Services</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="text-gold-600 mt-1 flex-shrink-0" size={20} />
                    <span className="text-gray-700">Local sightseeing arrangements</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="text-gold-600 mt-1 flex-shrink-0" size={20} />
                    <span className="text-gray-700">Temple visit coordination</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="text-gold-600 mt-1 flex-shrink-0" size={20} />
                    <span className="text-gray-700">Railway & airport transfers</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="text-gold-600 mt-1 flex-shrink-0" size={20} />
                    <span className="text-gray-700">Tour package assistance</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardContent className="p-8">
                <h3 className="text-xl font-bold text-navy-900 mb-4">Business Facilities</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="text-gold-600 mt-1 flex-shrink-0" size={20} />
                    <span className="text-gray-700">Conference & banquet halls</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="text-gold-600 mt-1 flex-shrink-0" size={20} />
                    <span className="text-gray-700">Audio-visual equipment</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="text-gold-600 mt-1 flex-shrink-0" size={20} />
                    <span className="text-gray-700">High-speed Wi-Fi</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="text-gold-600 mt-1 flex-shrink-0" size={20} />
                    <span className="text-gray-700">Business center services</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-navy-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6 font-serif">Experience Our Premium Facilities</h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Book your stay today and enjoy all our world-class amenities
          </p>
          <Link to="/booking">
            <button className="bg-gold-600 hover:bg-gold-700 text-white px-10 py-4 rounded-lg text-lg font-semibold transition-colors">
              Book Your Stay Now
            </button>
          </Link>
        </div>
      </section>
    </div>
    </>
  );
};

export default Facilities;
